import { ProtectedRoute } from '@components/ProtectedRoute/ProtectedRoute';
import type { SSOProvider } from '@components/SSOButton';
import { DemoPage } from '@pages/DemoPage';
import { LeadsPage } from '@pages/LeadsPage';
import { LoginPage } from '@pages/LoginPage';
import { ManageLeadsPage } from '@pages/ManageLeadsPage';
import { auth } from '@services/firebase/auth';
import { useAuthStore } from '@store/index';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import styles from './App.module.scss';

const AUTHENTICATION_TIMEOUT = 1500; // ms to wait before token refresh

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { initAuth, signInWithGoogle, isAuthenticated, signOut } = useAuthStore();
  // Blocks the auto-redirect useEffect while an SSO flow is in progress
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  // Shows the spinner in LoginPage during token refresh
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Initialize authentication listener
  useEffect(() => {
    const unsubscribe = initAuth();
    return () => unsubscribe();
  }, [initAuth]);

  // Handle SSO sign-in
  const handleSSOClick = async (provider: SSOProvider) => {
    if (provider === 'google') {
      try {
        setIsAuthenticating(true); // Block auto-redirect before the popup even opens
        await signInWithGoogle();
        setIsRefreshing(true); // Show spinner while waiting for Firebase to settle, then refresh token
        await new Promise((resolve) => setTimeout(resolve, AUTHENTICATION_TIMEOUT));
        await auth.currentUser?.getIdToken(true);
        const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
        navigate(from, { replace: true });
      } catch (error) {
        console.error('Sign-in failed:', error);
        await signOut();
      } finally {
        setIsAuthenticating(false);
        setIsRefreshing(false);
      }
    } else {
      console.warn(`${provider} SSO not implemented yet`);
    }
  };

  // Redirect to home if already authenticated and trying to access login.
  // Skipped while isAuthenticating to avoid racing with the SSO flow.
  useEffect(() => {
    if (isAuthenticated && !isAuthenticating && location.pathname === '/login') {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isAuthenticating, location.pathname, navigate]);

  return (
    <div className={styles.app}>
      <Routes>
        {/* Login page - only Google SSO */}
        <Route
          path="/login"
          element={
            <LoginPage
              ssoProviders={['google']}
              showEmailForm={false}
              isAuthenticating={isRefreshing}
              onSSOClick={handleSSOClick}
            />
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <ManageLeadsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leads"
          element={
            <ProtectedRoute>
              <LeadsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manage-leads"
          element={
            <ProtectedRoute>
              <ManageLeadsPage />
            </ProtectedRoute>
          }
        />

        {/* Demo/Developer Guide page - protected */}
        <Route
          path="/demo"
          element={
            <ProtectedRoute>
              <DemoPage />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
